import React, { useState } from 'react';
import './Support.css';

export const MAX_SCREENSHOT_BYTES = 3 * 1024 * 1024;
const ACCEPTED_SCREENSHOT_TYPES = new Set([
  'image/gif',
  'image/heic',
  'image/heif',
  'image/jpeg',
  'image/png',
  'image/webp'
]);

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(new Error('The screenshot could not be read.'));
    reader.readAsDataURL(file);
  });
}

function validateScreenshot(file) {
  if (!file) return null;
  if (!ACCEPTED_SCREENSHOT_TYPES.has(file.type)) {
    return 'Please choose a JPG, PNG, GIF, WebP, HEIC, or HEIF image.';
  }
  if (file.size > MAX_SCREENSHOT_BYTES) {
    return 'The screenshot must be 3 MB or smaller.';
  }
  return null;
}

function Support() {
  const appName = new URLSearchParams(window.location.search).get('app')?.slice(0, 200) || '';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    comments: '',
    screenshot: null,
    website: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState('');

  const handleChange = (event) => {
    const { name, value, files } = event.target;
    setFormData(previous => ({
      ...previous,
      [name]: files ? (files[0] || null) : value
    }));
  };

  const showError = (message) => {
    setSubmitStatus('error');
    setSubmitMessage(message);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitMessage('');
    setSubmitStatus('');

    const name = formData.name.trim();
    const email = formData.email.trim();
    const comments = formData.comments.trim();

    if (!name || !email || !comments) {
      showError('Please fill in all required fields.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showError('Please enter a valid email address.');
      return;
    }

    const screenshotError = validateScreenshot(formData.screenshot);
    if (screenshotError) {
      showError(screenshotError);
      return;
    }

    const endpoint = process.env.REACT_APP_SUPPORT_API_URL;
    if (!endpoint) {
      showError('Support is temporarily unavailable. Please try again later.');
      return;
    }

    setIsSubmitting(true);

    try {
      const screenshot = formData.screenshot ? {
        filename: formData.screenshot.name,
        contentType: formData.screenshot.type,
        data: await fileToBase64(formData.screenshot)
      } : null;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          comments,
          appName,
          screenshot,
          website: formData.website
        })
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.error || 'Your support request could not be sent.');
      }

      setSubmitStatus('success');
      setSubmitMessage(
        screenshot
          ? 'Your support request was sent with the screenshot attached.'
          : 'Your support request was sent.'
      );
      setFormData({ name: '', email: '', comments: '', screenshot: null, website: '' });

      const fileInput = document.getElementById('screenshot');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      showError(error.message || 'Your support request could not be sent. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="support">
      <section className="support-header">
        <h1>Support</h1>
        <p>Need help? We're here to assist you.</p>
      </section>

      <section className="support-content">
        <div className="content-wrapper">
          <div className="support-form-container">
            <div className="support-info">
              <h2>Contact Us</h2>
              <p>
                Having trouble with one of our apps? Have a suggestion or found a bug?
                We'd love to hear from you! Fill out the form below and we'll get back
                to you as soon as possible.
              </p>

              <div className="support-tips">
                <h3>📝 Tips for better support:</h3>
                <ul>
                  <li>Include screenshots when possible</li>
                  <li>Describe the steps that led to the issue</li>
                  <li>Mention which app and version you're using</li>
                  <li>Include your device model and OS version</li>
                </ul>
              </div>
            </div>

            <form className="support-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  maxLength="120"
                  autoComplete="name"
                  placeholder="Your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  maxLength="254"
                  autoComplete="email"
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="comments">Comments *</label>
                <textarea
                  id="comments"
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  required
                  maxLength="10000"
                  rows="6"
                  placeholder="Please describe your issue, suggestion, or question in detail..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="screenshot">Screenshot (optional)</label>
                <input
                  type="file"
                  id="screenshot"
                  name="screenshot"
                  onChange={handleChange}
                  accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif"
                />
                <small className="form-hint">
                  The image will be attached to your support email. Maximum size: 3 MB.
                </small>
              </div>

              <div className="website-field" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <button className="submit-button" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitMessage && (
                <div
                  className={`submit-message ${submitStatus}`}
                  role={submitStatus === 'error' ? 'alert' : 'status'}
                  aria-live="polite"
                >
                  {submitMessage}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Support;
